// ============================================================================
// medicAppointments.ts — ДАННЫЕ кабинета записи на приём (пациент/врач).
//
// SSoT — сервер: здесь только существующие RPC (MetadataService.ListTableData /
// GetDocument / GetCatalog / GetEnum, EntityService.ListEntities,
// AuthService.GetRoles) и штатные CreateTableRow/UpdateTableRow. Новых
// контрактов, миграций и полей НЕТ. Запись/перенос/отмена требуют связи:
// слот должен подтвердить сервер (fail-closed, без офлайн-очереди).
//
// Поддерживаются ДВА существующих источника записей:
//   • ядро «ГидраМедик» — документ medic_encounters (patient_id/doctor_id/
//     appointment_at/status) + справочник medic_patients;
//   • психология — документ psy_bookings (client_id/specialist_id/start_at/
//     duration_min/status) + справочник psy_clients. Занятость psy_bookings
//     проверяется СЕРВЕРОМ (модуль onBeforeSave).
//
// «Свободные слоты» — производная (клиент): рабочие часы + занятость выбранного
// специалиста. Рабочие часы — настройка пользователя (user_settings), потому
// что в существующих объектах расписания нет шаблона доступности. Занятость —
// всегда из серверных записей (fail-closed).
//
// Права — SSoT сервера: GetDocument/GetCatalog возвращают права текущей роли;
// UI лишь скрывает действия, которые сервер всё равно проверит.
// ============================================================================

import type { TableDataRow } from '@zdc/sdk';
import { getSDK } from '@/store/sdkStore';
import { useCenterStore } from '@/store/centerStore';
import { useAuthStore } from '@/store/authStore';
import { readThrough } from '@/lib/offlineCache';
import { settingsStore } from '@/lib/settingsStore';
import { formatValue } from '@/lib/format';
import {
  friendlyError,
  isNotFoundError,
  isPermissionDeniedError,
  logSwallowed,
} from '@/lib/errors';
import { isEffectivelyOffline } from '@/lib/offlineFlag';
import {
  MEDIC_DOCTOR_ROLE,
  MEDIC_NURSE_ROLE,
  MEDIC_REGISTRAR_ROLE,
  PSY_SPECIALIST_ROLES,
} from '@/lib/securityRoles';
export { MEDIC_DOCTOR_ROLE, MEDIC_NURSE_ROLE, MEDIC_REGISTRAR_ROLE, PSY_SPECIALIST_ROLES };

// --- Существующие объекты --------------------------------------------------

export const CORE_APPOINTMENT_DOC = 'medic_encounters';
export const CORE_PATIENT_CATALOG = 'medic_patients';
export const CORE_STATUS_ENUM = 'medic_encounter_status';
export const CORE_TYPE_ENUM = 'medic_encounter_type';

export const PSY_BOOKING_DOC = 'psy_bookings';
export const PSY_CLIENT_CATALOG = 'psy_clients';
export const PSY_STATUS_ENUM = 'PsyStatusZapisi';

export const MEDIC_APPOINTMENTS_APP_CODE = 'medic_appointments';

export const CORE_DEFAULT_DURATION = 30;
export const PSY_DEFAULT_DURATION = 60;

const SETTINGS_KEY = 'medic.appointments.settings';
const REMINDERS_KEY = 'medic.appointments.reminders';
const FIRED_KEY = 'hydra_medic_appointment_reminders';

// --- Типы ------------------------------------------------------------------

export type AppointmentSource = 'core' | 'psy';
export type AppointmentMode = 'patient' | 'doctor';
export type SourceState = 'ready' | 'denied' | 'unavailable' | 'error';
export type PatientIdentityState = SourceState | 'unlinked' | 'ambiguous';

export interface ObjectPermissions {
  canRead: boolean;
  canCreate: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  canViewMarked: boolean;
}

export interface SourceAvailability {
  available: boolean;
  state: SourceState;
  reason: string;
  rights: ObjectPermissions | null;
}

export interface AppointmentCapabilities {
  core: SourceAvailability;
  psy: SourceAvailability;
}

export interface AppointmentPerson {
  id: string;
  name: string;
  source: AppointmentSource;
  /** Все источники, в которых человек — специалист (врач/психолог). */
  sources: AppointmentSource[];
}

export interface AppointmentItem {
  /** Ключ строки: источник + id (id документов могут совпадать между таблицами). */
  key: string;
  id: string;
  source: AppointmentSource;
  startAt: string;
  endAt: string;
  durationMin: number;
  patientId: string;
  patientName: string;
  doctorId: string;
  doctorName: string;
  statusCode: string;
  statusLabel: string;
  typeCode: string;
  typeLabel: string;
  comment: string;
  number: string;
  /** Отметки серверных напоминаний psy_bookings (напоминания специалисту). */
  remindedDayAt: string;
  remindedHourAt: string;
  raw: Record<string, string>;
}

export interface AppointmentLoad {
  state: SourceState;
  items: AppointmentItem[];
  error: string;
  warnings: string[];
}

export interface PatientIdentity {
  source: AppointmentSource;
  state: PatientIdentityState;
  patient: AppointmentPerson | null;
  error: string;
  matchedBy: 'account' | 'phone' | 'email' | '';
}

export interface AppointmentIdentity {
  core: PatientIdentity;
  psy: PatientIdentity;
}

export interface AppointmentScheduleSettings {
  /** Шаг сетки, минут. */
  slotMinutes: number;
  workdayStart: string;
  workdayEnd: string;
  breakStart: string;
  breakEnd: string;
  /** 0=вс … 6=сб. */
  workdays: number[];
  /** Минимальный запас до начала записи, минут. */
  leadMinutes: number;
  /** Длительность по умолчанию для источника. */
  durations: Record<AppointmentSource, number>;
}

export interface AppointmentReminderSettings {
  enabled: boolean;
  /** За сколько часов напоминать (список). */
  leadHours: number[];
}

export interface AppointmentSlot {
  startAt: string;
  endAt: string;
  available: boolean;
  reason: 'past' | 'break' | 'busy' | 'closed' | '';
}

export interface AppointmentBookingInput {
  source: AppointmentSource;
  patientId: string;
  doctorId: string;
  startAt: string;
  durationMin: number;
  comment?: string;
  typeCode?: string;
}

export interface AppointmentStatusMeta {
  label: string;
  tone: string;
  active: boolean;
  final: boolean;
}

interface Row extends Record<string, string> {
  id: string;
}

interface IdentityUser {
  id: string;
  phone: string;
  email: string;
}

// --- Базовые настройки ------------------------------------------------------

export const DEFAULT_SCHEDULE_SETTINGS: AppointmentScheduleSettings = {
  slotMinutes: 30,
  workdayStart: '09:00',
  workdayEnd: '18:00',
  breakStart: '13:00',
  breakEnd: '14:00',
  workdays: [1, 2, 3, 4, 5, 6],
  leadMinutes: 30,
  durations: { core: CORE_DEFAULT_DURATION, psy: PSY_DEFAULT_DURATION },
};

export const DEFAULT_REMINDER_SETTINGS: AppointmentReminderSettings = {
  enabled: true,
  leadHours: [24, 2],
};

// --- Утилиты ----------------------------------------------------------------

function slug(): string {
  return useCenterStore.getState().baseSlug || 'local';
}

function cellsOf(row: TableDataRow): Record<string, string> {
  const out: Record<string, string> = {};
  for (const value of row.values ?? []) {
    const raw = value.value === null || value.value === undefined ? '' : String(value.value);
    out[String(value.column)] = raw === '<nil>' ? '' : raw;
  }
  return out;
}

function rowsOf(rows: TableDataRow[]): Row[] {
  return rows.map((row) => ({ id: String(row.id || ''), ...cellsOf(row) }));
}

function text(value: string | undefined): string {
  return String(value ?? '').trim();
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

/** Локальная дата (YYYY-MM-DD) без UTC-сдвига. */
export function localDateKey(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Локальная дата-время для записи (как у форм платформы: без часового пояса). */
export function localDateTime(date: Date): string {
  return `${localDateKey(date)}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

export function addMinutes(date: Date, minutes: number): Date {
  return new Date(date.getTime() + minutes * 60_000);
}

export function startOfDay(date: Date): Date {
  const out = new Date(date);
  out.setHours(0, 0, 0, 0);
  return out;
}

/** Значение сервера → локальные компоненты (часовой пояс конфигурации — formatValue). */
export function valueDate(value: string): Date | null {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const day = formatValue(raw, 'date:yyyy-MM-dd', 'timestamp');
  const time = formatValue(raw, 'date:HH:mm:ss', 'timestamp');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day) || !/^\d{2}:\d{2}:\d{2}$/.test(time)) {
    const parsed = new Date(raw.replace(' ', 'T'));
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  const parsed = new Date(`${day}T${time}`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function valueTime(value: string): string {
  return formatValue(value, 'date:HH:mm', 'timestamp') || '';
}

export function valueDateKey(value: string): string {
  return formatValue(value, 'date:yyyy-MM-dd', 'timestamp') || '';
}

export function valueDateTimeLabel(value: string): string {
  return formatValue(value, 'date:dd.MM.yyyy HH:mm', 'timestamp') || value || '—';
}

/** Подпись локально сгенерированного окна (без пересчёта часового пояса). */
export function localSlotLabel(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(String(value || ''));
  if (!match) return value || '—';
  return `${match[3]}.${match[2]}.${match[1]} ${match[4]}:${match[5]}`;
}

export function isUpcoming(value: string, now = new Date()): boolean {
  const date = valueDate(value);
  return !!date && date.getTime() >= now.getTime();
}

function durationOf(value: string | undefined, fallback: number): number {
  const parsed = Number.parseInt(String(value ?? ''), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

function isTruthy(value: string | undefined): boolean {
  const s = String(value ?? '').trim().toLowerCase();
  return s === 'true' || s === '1' || s === 'да';
}

function phoneDigits(value: string): string {
  return String(value || '').replace(/\D+/g, '');
}

function phoneVariants(value: string): string[] {
  const raw = String(value || '').trim();
  const digits = phoneDigits(raw);
  const out = new Set<string>([raw, digits]);
  if (digits.length === 11 && digits.startsWith('8')) out.add(`+7${digits.slice(1)}`);
  if (digits.length === 11 && digits.startsWith('7')) out.add(`8${digits.slice(1)}`);
  if (digits.length === 10) {
    out.add(`+7${digits}`);
    out.add(`8${digits}`);
  }
  out.delete('');
  return [...out];
}

function emailVariants(value: string): string[] {
  const raw = String(value || '').trim();
  const lower = raw.toLowerCase();
  return [...new Set([raw, lower].filter(Boolean))];
}

function personName(row: Row): string {
  const full = [row.last_name, row.first_name, row.middle_name]
    .map((part) => String(part || '').trim())
    .filter(Boolean)
    .join(' ');
  return text(row.name) || text(row.fio) || full || 'Пациент';
}

export function appointmentIdentityCacheKey(userId: string): string {
  return `medic-appointments:identity:${slug()}:${userId || 'anon'}`;
}

export function appointmentPatientCacheKey(source: AppointmentSource, patientId: string): string {
  const userId = useAuthStore.getState().user?.id || 'anon';
  return `medic-appointments:patient:${slug()}:${userId}:${source}:${patientId}`;
}

export function appointmentDoctorCacheKey(source: AppointmentSource, doctorId: string): string {
  const userId = useAuthStore.getState().user?.id || 'anon';
  return `medic-appointments:doctor:${slug()}:${userId}:${source}:${doctorId || 'all'}`;
}

// --- Права -----------------------------------------------------------------

function rightsOf(raw: unknown): ObjectPermissions | null {
  if (!raw || typeof raw !== 'object') return null;
  const value = raw as Record<string, unknown>;
  const flag = (snake: string, camel: string): boolean => {
    const v = value[snake] ?? value[camel];
    return v === undefined || v === null
      ? true
      : v === true || v === 'true' || v === '1' || v === 1;
  };
  return {
    canRead: flag('can_read', 'canRead'),
    canCreate: flag('can_create', 'canCreate'),
    canUpdate: flag('can_update', 'canUpdate'),
    canDelete: flag('can_delete', 'canDelete'),
    canViewMarked: flag('can_view_marked', 'canViewMarked'),
  };
}

/**
 * Право роли: сервер — SSoT. Если записи прав нет (null), ядро считает доступ
 * полным (пустой набор = все права) — показываем действие, сервер проверит.
 */
export function canDo(rights: ObjectPermissions | null, action: 'read' | 'create' | 'update' | 'delete'): boolean {
  if (!rights) return true;
  switch (action) {
    case 'read': return rights.canRead;
    case 'create': return rights.canCreate;
    case 'update': return rights.canUpdate;
    case 'delete': return rights.canDelete;
  }
}

// --- Доступность источников -------------------------------------------------

async function loadDocumentAvailability(code: string): Promise<SourceAvailability> {
  try {
    const doc = (await getSDK().metadata.getDocument({ id: code })) as unknown as Record<string, unknown>;
    return {
      available: true,
      state: 'ready',
      reason: '',
      rights: rightsOf((doc as { rights?: unknown }).rights),
    };
  } catch (e) {
    if (isNotFoundError(e)) {
      return { available: false, state: 'unavailable', reason: `Документ ${code} не найден`, rights: null };
    }
    if (isPermissionDeniedError(e)) {
      return { available: false, state: 'denied', reason: 'Нет прав на чтение документа приёмов', rights: null };
    }
    logSwallowed(e, `Запись на приём: доступность ${code}`);
    return { available: false, state: 'error', reason: friendlyError(e), rights: null };
  }
}

async function loadCatalogAvailability(code: string): Promise<SourceAvailability> {
  try {
    const catalog = (await getSDK().metadata.getCatalog({ id: code })) as unknown as Record<string, unknown>;
    return {
      available: true,
      state: 'ready',
      reason: '',
      rights: rightsOf((catalog as { rights?: unknown }).rights),
    };
  } catch (e) {
    if (isNotFoundError(e)) {
      return { available: false, state: 'unavailable', reason: `Справочник ${code} не найден`, rights: null };
    }
    if (isPermissionDeniedError(e)) {
      return { available: false, state: 'denied', reason: 'Нет прав на чтение справочника', rights: null };
    }
    logSwallowed(e, `Запись на приём: доступность ${code}`);
    return { available: false, state: 'error', reason: friendlyError(e), rights: null };
  }
}

/**
 * loadAppointmentCapabilities — какие источники записей реально есть в базе и
 * какие права у текущей роли. Ошибка одного источника не скрывает другой.
 */
export async function loadAppointmentCapabilities(): Promise<AppointmentCapabilities> {
  const userId = useAuthStore.getState().user?.id || 'anon';
  const result = await readThrough(`medic-appointments:capabilities:${slug()}:${userId}`, async () => {
    const [coreDoc, corePatients, psyDoc, psyClients] = await Promise.all([
      loadDocumentAvailability(CORE_APPOINTMENT_DOC),
      loadCatalogAvailability(CORE_PATIENT_CATALOG),
      loadDocumentAvailability(PSY_BOOKING_DOC),
      loadCatalogAvailability(PSY_CLIENT_CATALOG),
    ]);
    const core: SourceAvailability = {
      available: coreDoc.available && corePatients.available,
      state: coreDoc.available && corePatients.available ? 'ready' : (coreDoc.state !== 'ready' ? coreDoc.state : corePatients.state),
      reason: coreDoc.available ? corePatients.reason : coreDoc.reason,
      rights: coreDoc.rights,
    };
    const psy: SourceAvailability = {
      available: psyDoc.available && psyClients.available,
      state: psyDoc.available && psyClients.available ? 'ready' : (psyDoc.state !== 'ready' ? psyDoc.state : psyClients.state),
      reason: psyDoc.available ? psyClients.reason : psyDoc.reason,
      rights: psyDoc.rights,
    };
    return { core, psy };
  });
  return result.data;
}

// --- Подписи статусов/типов ------------------------------------------------

const statusLabelCache = new Map<string, Record<string, string>>();
const typeLabelCache = new Map<string, Record<string, string>>();

async function loadEnumLabels(code: string, cache: Map<string, Record<string, string>>): Promise<Record<string, string>> {
  const cacheKey = `${slug()}:${code}`;
  const cached = cache.get(cacheKey);
  if (cached) return cached;
  try {
    const response = await getSDK().metadata.getEnum({ code });
    const labels: Record<string, string> = {};
    for (const item of response.values || []) {
      labels[String(item.value)] = String(item.synonym || item.name || item.value);
    }
    cache.set(cacheKey, labels);
    return labels;
  } catch (e) {
    if (!isNotFoundError(e)) logSwallowed(e, `Запись на приём: перечисление ${code}`);
    return {};
  }
}

export function loadStatusLabels(source: AppointmentSource): Promise<Record<string, string>> {
  return loadEnumLabels(source === 'core' ? CORE_STATUS_ENUM : PSY_STATUS_ENUM, statusLabelCache);
}

export function loadTypeLabels(): Promise<Record<string, string>> {
  return loadEnumLabels(CORE_TYPE_ENUM, typeLabelCache);
}

const CORE_STATUSES: Record<string, AppointmentStatusMeta> = {
  planned: { label: 'Запланирован', tone: 'primary', active: true, final: false },
  done: { label: 'Выполнен', tone: 'success', active: false, final: true },
  cancelled: { label: 'Отменён', tone: 'danger', active: false, final: true },
};

const PSY_STATUSES: Record<string, AppointmentStatusMeta> = {
  new: { label: 'Новая', tone: 'primary', active: true, final: false },
  confirmed: { label: 'Подтверждена', tone: 'success', active: true, final: false },
  done: { label: 'Проведена', tone: 'muted', active: false, final: true },
  cancelled: { label: 'Отменена', tone: 'danger', active: false, final: true },
};

export function statusMeta(source: AppointmentSource, code: string, labels?: Record<string, string>): AppointmentStatusMeta {
  const map = source === 'core' ? CORE_STATUSES : PSY_STATUSES;
  const meta = map[code];
  if (meta) return { ...meta, label: labels?.[code] || meta.label };
  return {
    label: labels?.[code] || code || 'Статус не указан',
    tone: 'muted',
    active: !['done', 'cancelled', 'canceled', 'no_show'].includes(code),
    final: ['done', 'cancelled', 'canceled', 'no_show'].includes(code),
  };
}

export function statusToneClass(tone: string): string {
  switch (tone) {
    case 'primary': return 'bg-primary/10 text-primary border-primary/30';
    case 'success': return 'bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/30';
    case 'danger': return 'bg-destructive/10 text-destructive border-destructive/30';
    case 'warning': return 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30';
    default: return 'bg-muted text-muted-foreground border-border';
  }
}

export function isActiveStatus(source: AppointmentSource, code: string): boolean {
  return statusMeta(source, code).active;
}

// --- Настройки расписания/напоминаний --------------------------------------

export function loadScheduleSettings(): AppointmentScheduleSettings {
  const saved = settingsStore.getJSON<Partial<AppointmentScheduleSettings>>(SETTINGS_KEY, {});
  return {
    ...DEFAULT_SCHEDULE_SETTINGS,
    ...saved,
    durations: {
      ...DEFAULT_SCHEDULE_SETTINGS.durations,
      ...(saved.durations || {}),
      // У medic_encounters нет длительности: интервал приёма фиксирован 30 мин.
      core: CORE_DEFAULT_DURATION,
    },
    workdays: Array.isArray(saved.workdays) && saved.workdays.length > 0
      ? saved.workdays
      : DEFAULT_SCHEDULE_SETTINGS.workdays,
  };
}

export function saveScheduleSettings(settings: AppointmentScheduleSettings): void {
  settingsStore.setJSON(SETTINGS_KEY, settings);
}

export function loadReminderSettings(): AppointmentReminderSettings {
  const saved = settingsStore.getJSON<Partial<AppointmentReminderSettings>>(REMINDERS_KEY, {});
  return {
    enabled: saved.enabled === undefined ? DEFAULT_REMINDER_SETTINGS.enabled : !!saved.enabled,
    leadHours: Array.isArray(saved.leadHours) && saved.leadHours.length > 0
      ? saved.leadHours.filter((h) => Number.isFinite(h) && h > 0).sort((a, b) => b - a)
      : DEFAULT_REMINDER_SETTINGS.leadHours,
  };
}

export function saveReminderSettings(settings: AppointmentReminderSettings): void {
  settingsStore.setJSON(REMINDERS_KEY, settings);
}

// --- Разбор строк -----------------------------------------------------------

function toAppointment(row: Row, source: AppointmentSource, labels: Record<string, string>, typeLabels: Record<string, string>): AppointmentItem {
  const isCore = source === 'core';
  const startAt = isCore ? row.appointment_at || '' : row.start_at || '';
  const duration = durationOf(isCore ? '' : row.duration_min, isCore ? CORE_DEFAULT_DURATION : PSY_DEFAULT_DURATION);
  const start = valueDate(startAt);
  const endAt = start ? localDateTime(addMinutes(start, duration)) : '';
  const statusCode = row.status || '';
  const typeCode = isCore ? row.encounter_type || '' : '';
  return {
    key: `${source}:${row.id}`,
    id: row.id,
    source,
    startAt,
    endAt,
    durationMin: duration,
    patientId: isCore ? row.patient_id || '' : row.client_id || '',
    patientName: isCore ? row.patient_id_presentation || '' : row.client_id_presentation || '',
    doctorId: isCore ? row.doctor_id || '' : row.specialist_id || '',
    doctorName: isCore ? row.doctor_id_presentation || '' : row.specialist_id_presentation || '',
    statusCode,
    statusLabel: labels[statusCode] || row.status_presentation || statusCode || 'Статус не указан',
    typeCode,
    typeLabel: typeLabels[typeCode] || row.encounter_type_presentation || '',
    comment: row.comment || '',
    number: row.number || '',
    remindedDayAt: isCore ? '' : row.reminded_day_at || '',
    remindedHourAt: isCore ? '' : row.reminded_hour_at || '',
    raw: row,
  };
}

async function fetchRows(
  table: string,
  filters: Array<{ column: string; value: string }>,
  pageSize = 1000,
  orderBy = '',
  orderDesc = false,
): Promise<Row[]> {
  const response = await getSDK().metadata.listTableData({
    schema: 'public',
    table,
    pageSize,
    page: 0,
    filters,
    ...(orderBy ? { orderBy, orderDesc } : {}),
  });
  return rowsOf(response.rows || []).filter((row) => row.id !== '');
}

function sortAppointments(items: AppointmentItem[], now = new Date()): AppointmentItem[] {
  return [...items].sort((a, b) => {
    const aDate = valueDate(a.startAt);
    const bDate = valueDate(b.startAt);
    const aTime = aDate ? aDate.getTime() : 0;
    const bTime = bDate ? bDate.getTime() : 0;
    const aUpcoming = aTime >= now.getTime();
    const bUpcoming = bTime >= now.getTime();
    if (aUpcoming && bUpcoming) return aTime - bTime;
    if (aUpcoming) return -1;
    if (bUpcoming) return 1;
    return bTime - aTime;
  });
}

// --- Определение пациента ---------------------------------------------------

interface LookupProbe {
  column: string;
  value: string;
}

async function lookupRows(table: string, probe: LookupProbe): Promise<Row[]> {
  return fetchRows(table, [{ column: probe.column, value: probe.value }], 50);
}

async function resolvePatient(
  source: AppointmentSource,
  user: IdentityUser,
): Promise<PatientIdentity> {
  const table = source === 'core' ? CORE_PATIENT_CATALOG : PSY_CLIENT_CATALOG;
  const hasAccountColumn = source === 'psy';
  const probes: LookupProbe[] = [];
  if (hasAccountColumn && user.id) probes.push({ column: 'user_id', value: user.id });
  for (const value of phoneVariants(user.phone)) probes.push({ column: 'phone', value });
  for (const value of emailVariants(user.email)) probes.push({ column: 'email', value });

  if (probes.length === 0) {
    return { source, state: 'unlinked', patient: null, error: '', matchedBy: '' };
  }

  let denied = false;
  let unavailable = false;
  let failed: unknown = null;
  const found = new Map<string, { row: Row; matchedBy: 'account' | 'phone' | 'email' }>();

  for (const probe of probes) {
    try {
      const rows = await lookupRows(table, probe);
      for (const row of rows) {
        if (!row.id || isTruthy(row.is_group)) continue;
        if (hasAccountColumn && row.user_id && user.id && row.user_id !== user.id) continue;
        const matchedBy: 'account' | 'phone' | 'email' = probe.column === 'user_id'
          ? 'account'
          : probe.column === 'phone' ? 'phone' : 'email';
        const existing = found.get(row.id);
        if (!existing || (existing.matchedBy !== 'account' && matchedBy === 'account')) {
          found.set(row.id, { row, matchedBy });
        }
      }
    } catch (e) {
      if (isPermissionDeniedError(e)) denied = true;
      else if (isNotFoundError(e)) unavailable = true;
      else if (!failed) failed = e;
    }
  }

  if (found.size > 1) {
    return { source, state: 'ambiguous', patient: null, error: '', matchedBy: '' };
  }
  if (found.size === 1) {
    const [{ row, matchedBy }] = [...found.values()];
    return {
      source,
      state: 'ready',
      patient: { id: row.id, name: personName(row), source, sources: [source] },
      error: '',
      matchedBy,
    };
  }
  if (denied) {
    return { source, state: 'denied', patient: null, error: 'Нет прав на чтение картотеки пациентов', matchedBy: '' };
  }
  if (failed) {
    return { source, state: 'error', patient: null, error: friendlyError(failed), matchedBy: '' };
  }
  if (unavailable) {
    return { source, state: 'unavailable', patient: null, error: 'Картотека пациентов недоступна', matchedBy: '' };
  }
  return { source, state: 'unlinked', patient: null, error: '', matchedBy: '' };
}

/**
 * resolveAppointmentIdentity — карточка пациента, связанная с учётной записью.
 * medic_patients не имеет реквизита user_id → связь по контактам; psy_clients
 * сначала по явной привязке user_id, затем по контактам.
 */
export async function resolveAppointmentIdentity(user: IdentityUser): Promise<AppointmentIdentity> {
  const cached = await readThrough(
    appointmentIdentityCacheKey(user.id),
    async () => {
      const [core, psy] = await Promise.all([
        resolvePatient('core', user),
        resolvePatient('psy', user),
      ]);
      return { core, psy };
    },
  );
  return cached.data;
}

// --- Специалисты ------------------------------------------------------------

interface RoleLike { id?: string; name?: string }

/**
 * loadSpecialists — люди с ролями специалистов (Врач/регистратор/психолог).
 * Роли и состав — SSoT сервера (auth.getRoles + entity.listEntities(roleId)).
 * Если ролей специалистов нет — явный пустой список (UI объяснит причину).
 */
export async function loadSpecialists(
  capabilities: AppointmentCapabilities,
): Promise<{ items: AppointmentPerson[]; error: string }> {
  try {
    const api = getSDK();
    const rolesResponse = await api.auth.getRoles({});
    const roles = (rolesResponse.items || []) as RoleLike[];
    const specialistRoles = roles.filter((role) => {
      const name = String(role.name || '');
      return name === MEDIC_DOCTOR_ROLE
        || (PSY_SPECIALIST_ROLES as readonly string[]).includes(name);
    });
    if (specialistRoles.length === 0) {
      return { items: [], error: 'В базе нет ролей «Врач» или «Психолог» — назначать приёмы некому' };
    }

    const lists = await Promise.all(specialistRoles.map(async (role) => {
      const response = await api.entity.listEntities({ roleId: String(role.id || '') });
      return { role, items: (response.items || []) as Array<Record<string, unknown>> };
    }));

    const byId = new Map<string, AppointmentPerson>();
    for (const { role, items } of lists) {
      const roleName = String(role.name || '');
      for (const item of items) {
        const id = String(item.id || '');
        if (!id) continue;
        const activeFlag = item.isActive ?? item.is_active;
        if (activeFlag === false || activeFlag === 'false' || activeFlag === 0) continue;
        const rawName = String(item.name || item.login || '').trim();
        const name = rawName && rawName !== '<nil>' ? rawName : 'Специалист';
        const existing = byId.get(id);
        const source: AppointmentSource = roleName === MEDIC_DOCTOR_ROLE ? 'core' : 'psy';
        if (!existing) {
          byId.set(id, { id, name, source, sources: [source] });
        } else {
          // Человек с двумя ролями — доступен в обоих источниках; отдельная
          // запись в списке не нужна, источник выбирается по режиму.
          const sources = [...new Set([...existing.sources, source])];
          byId.set(id, { ...existing, name, sources, source: sources[0] });
        }
      }
    }

    const items = [...byId.values()].filter((person) =>
      person.sources.some((src) => capabilities[src].available),
    ).sort((a, b) => a.name.localeCompare(b.name, 'ru'));
    return {
      items,
      error: items.length === 0
        ? 'Нет пользователей с ролями «Врач» или «Психолог» — назначьте роль в конфигураторе.'
        : '',
    };
  } catch (e) {
    if (isPermissionDeniedError(e)) return { items: [], error: 'Нет прав на список специалистов' };
    logSwallowed(e, 'Запись на приём: специалисты');
    return { items: [], error: friendlyError(e) };
  }
}

// --- Загрузка записей -------------------------------------------------------

async function loadSourceAppointments(
  source: AppointmentSource,
  filters: Array<{ column: string; value: string }>,
  cacheKey = '',
): Promise<AppointmentItem[]> {
  const load = async (): Promise<AppointmentItem[]> => {
    const [statusLabels, typeLabels] = await Promise.all([
      loadStatusLabels(source),
      source === 'core' ? loadTypeLabels() : Promise.resolve({}),
    ]);
    const table = source === 'core' ? CORE_APPOINTMENT_DOC : PSY_BOOKING_DOC;
    // Последние 1000 записей: сортировка по времени вниз, иначе при большом
    // объёме первые страницы могут не содержать предстоящих записей.
    const rows = await fetchRows(table, filters, 1000, source === 'core' ? 'appointment_at' : 'start_at', true);
    return rows.map((row) => toAppointment(row, source, statusLabels, typeLabels));
  };
  if (!cacheKey) return load();
  const cached = await readThrough(cacheKey, load);
  return cached.data;
}

/** Записи конкретного пациента в выбранном источнике. */
export async function loadPatientAppointments(
  identity: AppointmentIdentity,
  capabilities: AppointmentCapabilities,
): Promise<AppointmentLoad> {
  const items: AppointmentItem[] = [];
  const warnings: string[] = [];
  let denied = false;
  let unavailable = 0;
  let succeeded = 0;
  let error = '';
  const availableSources = (['core', 'psy'] as AppointmentSource[]).filter((src) => capabilities[src].available);

  await Promise.all(availableSources.map(async (source) => {
    const person = identity[source].patient;
    if (!person) return;
    try {
      const filters = source === 'core'
        ? [{ column: 'patient_id', value: person.id }]
        : [{ column: 'client_id', value: person.id }];
      const loaded = await loadSourceAppointments(source, filters, appointmentPatientCacheKey(source, person.id));
      items.push(...loaded);
      succeeded += 1;
    } catch (e) {
      if (isPermissionDeniedError(e)) {
        denied = true;
        warnings.push(source === 'core' ? 'приёмы: нет прав чтения' : 'записи к специалисту: нет прав чтения');
      } else if (isNotFoundError(e)) {
        unavailable += 1;
      } else {
        error = error || friendlyError(e);
      }
    }
  }));

  if (items.length > 0 || succeeded > 0) {
    return { state: 'ready', items: sortAppointments(items), error: '', warnings };
  }
  if (denied) return { state: 'denied', items: [], error: 'Нет прав на чтение записей', warnings };
  if (error) return { state: 'error', items: [], error, warnings };
  if (availableSources.length > 0 && unavailable === availableSources.length) {
    return { state: 'unavailable', items: [], error: 'Документы записей недоступны', warnings };
  }
  return { state: 'ready', items: [], error: '', warnings };
}

/**
 * loadDoctorAppointments — записи выбранного специалиста (или ВСЕ, если id пуст:
 * администратор/регистратор). Используется и для дневной сетки, и для списка.
 */
export async function loadDoctorAppointments(
  doctorId: string,
  capabilities: AppointmentCapabilities,
  useCache = true,
): Promise<AppointmentLoad> {
  const items: AppointmentItem[] = [];
  const warnings: string[] = [];
  let denied = false;
  let succeeded = 0;
  let error = '';
  const sources = (['core', 'psy'] as AppointmentSource[]).filter((source) => capabilities[source].available);

  await Promise.all(sources.map(async (source) => {
    try {
      const filters = doctorId
        ? [{ column: source === 'core' ? 'doctor_id' : 'specialist_id', value: doctorId }]
        : [];
      const loaded = await loadSourceAppointments(source, filters,
        useCache ? appointmentDoctorCacheKey(source, doctorId) : '');
      items.push(...loaded);
      succeeded += 1;
    } catch (e) {
      if (isPermissionDeniedError(e)) {
        denied = true;
        warnings.push(source === 'core' ? 'приёмы: нет прав чтения' : 'записи: нет прав чтения');
      } else if (!isNotFoundError(e)) {
        error = error || friendlyError(e);
      }
    }
  }));

  if (items.length > 0 || succeeded > 0) {
    return { state: 'ready', items: sortAppointments(items), error: '', warnings };
  }
  if (denied) return { state: 'denied', items: [], error: 'Нет прав на чтение записей', warnings };
  if (error) return { state: 'error', items: [], error, warnings };
  return { state: 'ready', items: [], error: '', warnings };
}

// --- Пациенты для записи врачом --------------------------------------------

export async function loadPatientOptions(
  source: AppointmentSource,
  capabilities: AppointmentCapabilities,
): Promise<{ items: AppointmentPerson[]; error: string }> {
  if (!capabilities[source].available) {
    return { items: [], error: 'Источник записей недоступен' };
  }
  const table = source === 'core' ? CORE_PATIENT_CATALOG : PSY_CLIENT_CATALOG;
  try {
    const rows = (await fetchRows(table, [], 1000)).filter((row) => !isTruthy(row.is_group));
    return {
      items: rows.map((row) => ({ id: row.id, name: personName(row), source, sources: [source] }))
        .sort((a, b) => a.name.localeCompare(b.name, 'ru')),
      error: '',
    };
  } catch (e) {
    if (isPermissionDeniedError(e)) return { items: [], error: 'Нет прав на картотеку пациентов' };
    logSwallowed(e, `Запись на приём: пациенты ${table}`);
    return { items: [], error: friendlyError(e) };
  }
}

// --- Слоты ------------------------------------------------------------------

function parseClock(value: string): { hour: number; minute: number } | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(String(value || '').trim());
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
  return { hour, minute };
}

function atClock(date: Date, clock: string): Date | null {
  const parsed = parseClock(clock);
  if (!parsed) return null;
  const out = new Date(date);
  out.setHours(parsed.hour, parsed.minute, 0, 0);
  return out;
}

function overlaps(start: number, end: number, otherStart: number, otherEnd: number): boolean {
  return start < otherEnd && otherStart < end;
}

/**
 * buildDaySlots — сетка окон на день для ОДНОГО специалиста и источника.
 * Занятость — из активных серверных записей (все, кроме отменённых/завершённых
 * для psy; для core — все, кроме отменённых). Рабочие часы — настройка.
 */
export function buildDaySlots(
  dateKey: string,
  source: AppointmentSource,
  durationMin: number,
  busy: AppointmentItem[],
  settings: AppointmentScheduleSettings,
  now = new Date(),
): { slots: AppointmentSlot[]; closed: boolean } {
  const day = new Date(`${dateKey}T00:00:00`);
  if (Number.isNaN(day.getTime())) return { slots: [], closed: true };
  if (!settings.workdays.includes(day.getDay())) return { slots: [], closed: true };

  const start = atClock(day, settings.workdayStart);
  const end = atClock(day, settings.workdayEnd);
  const breakStart = atClock(day, settings.breakStart);
  const breakEnd = atClock(day, settings.breakEnd);
  if (!start || !end || end.getTime() <= start.getTime()) return { slots: [], closed: true };

  const step = Math.max(5, Number(settings.slotMinutes) || DEFAULT_SCHEDULE_SETTINGS.slotMinutes);
  const duration = source === 'core'
    ? CORE_DEFAULT_DURATION
    : Math.max(5, Number(durationMin) || settings.durations[source]);
  const leadUntil = now.getTime() + Math.max(0, settings.leadMinutes) * 60_000;

  const busyIntervals = busy
    .filter((item) => item.source === source)
    .filter((item) => item.statusCode !== 'cancelled' && item.statusCode !== 'canceled')
    .map((item) => {
      const itemStart = valueDate(item.startAt);
      if (!itemStart) return null;
      return {
        start: itemStart.getTime(),
        end: itemStart.getTime() + Math.max(1, item.durationMin || duration) * 60_000,
      };
    })
    .filter((interval): interval is { start: number; end: number } => interval !== null);

  const slots: AppointmentSlot[] = [];
  for (let cursor = new Date(start); cursor.getTime() + duration * 60_000 <= end.getTime(); cursor = addMinutes(cursor, step)) {
    const slotStart = cursor.getTime();
    const slotEnd = slotStart + duration * 60_000;
    let reason: AppointmentSlot['reason'] = '';
    if (slotEnd <= leadUntil || slotStart < leadUntil) {
      reason = 'past';
    } else if (breakStart && breakEnd && overlaps(slotStart, slotEnd, breakStart.getTime(), breakEnd.getTime())) {
      reason = 'break';
    } else if (busyIntervals.some((interval) => overlaps(slotStart, slotEnd, interval.start, interval.end))) {
      reason = 'busy';
    }
    slots.push({
      startAt: localDateTime(cursor),
      endAt: localDateTime(addMinutes(cursor, duration)),
      available: reason === '',
      reason,
    });
  }
  return { slots, closed: false };
}

export function slotReasonLabel(reason: AppointmentSlot['reason']): string {
  switch (reason) {
    case 'past': return 'время прошло';
    case 'break': return 'перерыв';
    case 'busy': return 'занято';
    case 'closed': return 'выходной';
    default: return 'свободно';
  }
}

// --- Изменения --------------------------------------------------------------

function assertOnline(): void {
  if (isEffectivelyOffline()) {
    throw new Error('Запись на приём требует связи с сервером: слот должен подтвердить сервер. Отключите автономный режим и повторите.');
  }
}

function toValues(data: Record<string, unknown>): Array<{ column: string; value: string }> {
  return Object.entries(data)
    .filter(([, value]) => value !== undefined && value !== null)
    .map(([column, value]) => ({ column, value: String(value) }));
}

async function assertSlotStillFree(input: AppointmentBookingInput, excludeKey = ''): Promise<void> {
  const start = valueDate(input.startAt);
  if (!start) throw new Error('Не удалось определить время записи');
  const duration = input.source === 'core'
    ? CORE_DEFAULT_DURATION
    : Math.max(5, input.durationMin || PSY_DEFAULT_DURATION);
  const startMs = start.getTime();
  const endMs = startMs + duration * 60_000;
  const rows = await loadSourceAppointments(input.source, [{
    column: input.source === 'core' ? 'doctor_id' : 'specialist_id',
    value: input.doctorId,
  }]);
  for (const row of rows) {
    if (excludeKey && row.key === excludeKey) continue;
    if (row.statusCode === 'cancelled' || row.statusCode === 'canceled') continue;
    const rowStart = valueDate(row.startAt);
    if (!rowStart) continue;
    const rowMs = rowStart.getTime();
    const rowEndMs = rowMs + Math.max(1, row.durationMin || duration) * 60_000;
    if (startMs < rowEndMs && rowMs < endMs) {
      throw new Error(`Время уже занято: у специалиста есть запись на ${valueTime(row.startAt)}. Выберите другое окно.`);
    }
  }
}

export async function createAppointment(input: AppointmentBookingInput): Promise<string> {
  assertOnline();
  // Повторная проверка непосредственно перед записью: между построением сетки
  // и сохранением слот мог занять другой пользователь.
  await assertSlotStillFree(input);
  const isCore = input.source === 'core';
  const values: Record<string, unknown> = isCore
    ? {
      patient_id: input.patientId,
      doctor_id: input.doctorId,
      appointment_at: input.startAt,
      encounter_type: input.typeCode || 'med_visit',
      status: 'planned',
    }
    : {
      client_id: input.patientId,
      specialist_id: input.doctorId,
      start_at: input.startAt,
      duration_min: input.durationMin,
      status: 'new',
      comment: input.comment || '',
    };
  // Прямая запись на сервер (без офлайн-очереди): время приёма должно быть
  // подтверждено сервером, иначе возможны две записи на один слот.
  const created = await getSDK().metadata.createTableRow({
    schema: 'public',
    table: isCore ? CORE_APPOINTMENT_DOC : PSY_BOOKING_DOC,
    values: toValues(values),
  });
  return String(created.id || '');
}

export async function rescheduleAppointment(item: AppointmentItem, startAt: string): Promise<void> {
  assertOnline();
  await assertSlotStillFree({
    source: item.source,
    patientId: item.patientId,
    doctorId: item.doctorId,
    startAt,
    durationMin: item.durationMin,
  }, item.key);
  const values = item.source === 'core'
    ? { appointment_at: startAt }
    : { start_at: startAt, duration_min: item.durationMin };
  await getSDK().metadata.updateTableRow({
    schema: 'public',
    table: item.source === 'core' ? CORE_APPOINTMENT_DOC : PSY_BOOKING_DOC,
    rowId: item.id,
    values: toValues(values),
  });
}

export async function setAppointmentStatus(
  item: AppointmentItem,
  status: string,
  comment?: string,
): Promise<void> {
  assertOnline();
  const values: Record<string, unknown> = { status };
  // У medic_encounters нет колонки comment — причину пишем только в psy_bookings.
  if (item.source === 'psy' && comment && comment.trim()) {
    values.comment = item.comment ? `${item.comment}\n${comment.trim()}` : comment.trim();
  }
  await getSDK().metadata.updateTableRow({
    schema: 'public',
    table: item.source === 'core' ? CORE_APPOINTMENT_DOC : PSY_BOOKING_DOC,
    rowId: item.id,
    values: toValues(values),
  });
}

/** Завершение приёма (врач). */
export async function completeAppointment(item: AppointmentItem): Promise<void> {
  await setAppointmentStatus(item, 'done', 'Приём завершён');
}

export function canPatientChange(item: AppointmentItem, now = new Date()): boolean {
  if (!isActiveStatus(item.source, item.statusCode)) return false;
  const date = valueDate(item.startAt);
  return !!date && date.getTime() > now.getTime();
}

export function canReschedule(item: AppointmentItem, rights: ObjectPermissions | null, now = new Date()): boolean {
  return canDo(rights, 'update') && canPatientChange(item, now);
}

export function canCancel(item: AppointmentItem, rights: ObjectPermissions | null, now = new Date()): boolean {
  return canDo(rights, 'update') && canPatientChange(item, now);
}

// --- Напоминания ------------------------------------------------------------

export interface AppointmentReminder {
  key: string;
  item: AppointmentItem;
  at: Date;
  leadHours: number;
  label: string;
}

/** Ближайшие напоминания (по настройке leadHours) для предстоящих записей. */
export function buildReminders(
  items: AppointmentItem[],
  settings: AppointmentReminderSettings,
  now = new Date(),
  horizonDays = 7,
): AppointmentReminder[] {
  const out: AppointmentReminder[] = [];
  const horizon = now.getTime() + horizonDays * 86_400_000;
  for (const item of items) {
    if (!isActiveStatus(item.source, item.statusCode)) continue;
    const start = valueDate(item.startAt);
    if (!start || start.getTime() < now.getTime()) continue;
    for (const leadHours of settings.leadHours) {
      const at = new Date(start.getTime() - leadHours * 3_600_000);
      if (at.getTime() < now.getTime() || at.getTime() > horizon) continue;
      out.push({
        key: `${useAuthStore.getState().user?.id || 'anon'}:${item.key}:${leadHours}:${item.startAt}`,
        item,
        at,
        leadHours,
        label: leadHours >= 24
          ? `за ${Math.round(leadHours / 24)} дн.`
          : `за ${leadHours} ч.`,
      });
    }
  }
  return out.sort((a, b) => a.at.getTime() - b.at.getTime());
}

export function readFiredReminders(): Set<string> {
  try {
    const raw = localStorage.getItem(FIRED_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return new Set(Array.isArray(parsed) ? parsed.map(String) : []);
  } catch (e) {
    logSwallowed(e, 'Запись на приём: прочитанные напоминания');
    return new Set();
  }
}

export function markReminderFired(key: string): void {
  try {
    const fired = readFiredReminders();
    fired.add(key);
    const keep = [...fired].slice(-200);
    localStorage.setItem(FIRED_KEY, JSON.stringify(keep));
  } catch (e) {
    logSwallowed(e, 'Запись на приём: отметка напоминания');
  }
}

/** Отметка серверного напоминания psy_bookings (напоминания специалисту). */
export function serverReminderLabel(item: AppointmentItem, leadHours: number): string {
  if (item.source !== 'psy') return '';
  const value = leadHours >= 24 ? item.remindedDayAt : item.remindedHourAt;
  if (value) return `отправлено ${valueDateTimeLabel(value)}`;
  return 'запланировано';
}
